import { Injectable } from '@angular/core';
import { Internal } from '../decorators/app.decorators';
import { PcsSizingEngineResponse } from 'pcs-domain';
import { UsageBreakdown } from '../../feature-modules/output-components/usage-summary-bar/usage-summary-bar.component';
import { PCSSizerSerive } from './pcsizer.service';

/**
 * Shared cluster-level Memory/Storage usage math for the Summary cards on both
 * Management Domain and Workload Domain - moved here so both call the same
 * implementation instead of maintaining two copies of the same formulas.
 */
@Internal()
@Injectable({
  providedIn: 'root'
})
export class ClusterUsageCalculationService {

  constructor(private pcsSizer: PCSSizerSerive) { }

  /**
   * vSAN OSA has no objectOverhead field - unlike ESA, its "Used by Workloads" figure must be
   * built from fileSystemOverhead/diskFormatOverhead/checksumOverhead/compressionOnlyOverhead
   * instead. Detected from the clusters themselves so no call site needs to pass this in.
   */
  private isOsaArchitecture(clustersToCount: PcsSizingEngineResponse.Cluster[]): boolean {
    return !!this.pcsSizer.vsanArchType(clustersToCount[0]?.vsanArchitecture)?.includes('OSA');
  }

  private getRawCapacityValue(clusters: PcsSizingEngineResponse.Cluster[]): number {
    clusters = (clusters ?? []).filter(c => c.clusterType !== 'CON');
    let totalRawCapacity = 0;
    const nonWitnessClusters = clusters.filter(c => c.siteType !== 'WITNESS');
    const clustersToCount = nonWitnessClusters.length > 0 ? nonWitnessClusters : clusters;
    clustersToCount.forEach(c => totalRawCapacity += parseFloat(c.clusterConfiguration?.rawCapacity?.value?.toString() || '0'));
    return totalRawCapacity;
  }

  /**
   * Memory Usage breakdown (RAW/Overheads/Used by Workloads/Remaining) for the Summary card.
   * Overheads = vSAN memory overhead per host x hosts; Used by Workloads = memory used by
   * applications per host x hosts; Remaining = RAW - Overheads - Used.
   * Summed per-cluster (not siteClusters[0] x total hosts) since a multi-cluster domain's
   * clusters can have different per-host memory/overhead/requiredMemory values.
   *
   * requiredMemory (per host) already includes vsanMemoryOverheadPerHost - confirmed against
   * the sizing engine's own resourceStats.memory% (e.g. (813.26 - 58.97) / 832 = 90.68%, matching
   * a real response's reported 90.66%). Since overheads are already counted separately below, the
   * overhead portion must be subtracted out of requiredMemory here to avoid double-counting it,
   * which previously made usedByWorkloads blow past usable capacity and pin Remaining to 0.
   */
  getMemoryUsageBreakdown(siteClusters: PcsSizingEngineResponse.Cluster[]): UsageBreakdown | undefined {
    if (!siteClusters || !siteClusters[0]) return undefined;
    const nonWitnessClusters = siteClusters.filter(c => c.siteType !== 'WITNESS');
    const clustersToCount = nonWitnessClusters.length > 0 ? nonWitnessClusters : siteClusters;
    const unit = clustersToCount[0]?.serverConfiguration?.memory?.units;
    if (!unit) return undefined;

    let raw = 0;
    let overheads = 0;
    let usedByWorkloads = 0;
    clustersToCount.forEach(cluster => {
      const server = cluster.serverConfiguration;
      const hosts = parseFloat(cluster.clusterConfiguration?.nodesSize?.value?.toString() || '0');
      const overheadPerHost = parseFloat(server.vsanMemoryOverheadPerHost?.value?.toString() || '0');
      const requiredPerHost = parseFloat(server.requiredMemory?.value?.toString() || '0');
      raw += parseFloat(server.memory?.value?.toString() || '0') * hosts;
      overheads += overheadPerHost * hosts;
      usedByWorkloads += Math.max(requiredPerHost - overheadPerHost, 0) * hosts;
    });
    // Required memory can exceed usable capacity when a domain is overcommitted - cap it at
    // what's actually usable so Overheads + Used + Remaining never exceeds RAW (100%) in the bar.
    usedByWorkloads = Math.min(usedByWorkloads, Math.max(raw - overheads, 0));
    const remaining = Math.max(raw - overheads - usedByWorkloads, 0);
    return {
      raw,
      usable: raw - overheads,
      unit,
      overheads,
      usedByWorkloads,
      remaining
    };
  }

  /**
   * Sums the storage overhead fields that make up the Summary card's "Overheads" %.
   * Deliberately excludes dedupOverhead (shown separately as "Total Dedupe Overhead") and
   * excludes "Effective Used Capacity" (diskSpaceUsageOverheads.rawStorage). Fields are only
   * summed when present, so this naturally adapts across ESA vs OSA responses.
   */
  private getStorageOverheadsSum(siteClusters: PcsSizingEngineResponse.Cluster[]): number {
    siteClusters = (siteClusters ?? []).filter(c => c.clusterType !== 'CON');
    const nonWitnessClusters = siteClusters.filter(c => c.siteType !== 'WITNESS');
    const clustersToCount = nonWitnessClusters.length > 0 ? nonWitnessClusters : siteClusters;
    let sum = 0;
    clustersToCount.forEach(c => {
      const overheads = c.diskSpaceUsageOverheads;
      if (overheads?.objectOverhead) sum += parseFloat(overheads.objectOverhead.value);
      if (overheads?.fileSystemOverhead) sum += parseFloat(overheads.fileSystemOverhead.value);
      if (overheads?.perfTierOverhead) sum += parseFloat(overheads.perfTierOverhead.value);
      if (c.clusterConfiguration?.operationsReserve) sum += parseFloat(c.clusterConfiguration.operationsReserve.value);
      if (c.clusterConfiguration?.metadataOverhead) sum += parseFloat(c.clusterConfiguration.metadataOverhead.value);
    });
    return sum;
  }

  getStorageOverheadsPercent(siteClusters: PcsSizingEngineResponse.Cluster[]): number {
    const rawCapacity = this.getRawCapacityValue(siteClusters);
    if (!rawCapacity) return 0;
    return (this.getStorageOverheadsSum(siteClusters) / rawCapacity) * 100;
  }

  /** Extra reserve above operationsReserve, for the ESA Storage Usage bar only. */
  private getEsaAdditionalReserve(cluster: PcsSizingEngineResponse.Cluster): number {
    if (this.pcsSizer.vsanArchType(cluster.vsanArchitecture) !== 'vSAN ESA') return 0;
    const server = cluster.serverConfiguration;
    const rawPerHost = parseFloat(server?.rawCapacityNoOverheads?.value ?? '');
    const usablePerHost = parseFloat(server?.rawCapacity?.value ?? '');
    const hosts = parseFloat(cluster.clusterConfiguration?.nodesSize?.value ?? '');
    // Older saved responses cannot reconstruct the applied reserve; retain operationsReserve.
    if (![rawPerHost, usablePerHost, hosts].every(Number.isFinite) || usablePerHost <= 0) return 0;
    const diskOverheads = cluster.diskSpaceUsageOverheads;
    const fileSystem = parseFloat(diskOverheads?.fileSystemOverhead?.value || '0');
    const perfTier = parseFloat(diskOverheads?.perfTierOverhead?.value || '0');
    const operationsReserve = parseFloat(cluster.clusterConfiguration?.operationsReserve?.value || '0');
    // The capacity gap contains FS + perf tier + max(free-space reserve, operations reserve).
    // Remove the two itemized overheads, which the bar already counts separately.
    const appliedReserve = hosts * Math.max(rawPerHost - usablePerHost, 0) - fileSystem - perfTier;
    return Math.max(appliedReserve, operationsReserve) - operationsReserve;
  }

  getTotalDedupeOverhead(siteClusters: PcsSizingEngineResponse.Cluster[]): string {
    siteClusters = (siteClusters ?? []).filter(c => c.clusterType !== 'CON');
    const nonWitnessClusters = siteClusters.filter(c => c.siteType !== 'WITNESS');
    const clustersToCount = nonWitnessClusters.length > 0 ? nonWitnessClusters : siteClusters;
    let sum = 0;
    let units = '';
    clustersToCount.forEach(c => {
      if (c.diskSpaceUsageOverheads?.dedupOverhead) {
        sum += parseFloat(c.diskSpaceUsageOverheads.dedupOverhead.value);
        units = c.diskSpaceUsageOverheads.dedupOverhead.units || units;
      }
    });
    return units ? sum.toFixed(2) + " " + units : "";
  }

  /**
   * Storage Usage breakdown (RAW/Overheads/Used by Workloads/Remaining) for the Summary card.
   * RAW Storage = clusterConfiguration.rawCapacity (same value as the "Total Raw Storage" stat);
   * Remaining = RAW - Overheads - Used.
   *
   * Used by Workloads:
   * - for vSAN ESA: clusterConfiguration.rawStorageWithOverheads, which includes workload demand
   *   and object overhead with RAID applied. The itemized objectOverhead has no RAID multiplier,
   *   so adding it to diskSpaceUsageOverheads.rawStorage undercounts demand. Older responses without
   *   a valid demand field retain rawStorage + objectOverhead. Object overhead is removed from the
   *   bar's Overheads bucket in either case, while getStorageOverheadsPercent() keeps its existing sum.
   *   The reserve in the ESA Overheads bucket is the larger of free-space reserve and operations
   *   reserve, reconstructed from the server capacity gap when available.
   * - for vSAN OSA: objectOverhead doesn't exist, so the equivalent "used" overhead fields are
   *   fileSystemOverhead + diskFormatOverhead + checksumOverhead + compressionOnlyOverhead instead.
   *   fileSystemOverhead is also removed from the Overheads bucket to avoid double-counting it (the
   *   other three were never part of that bucket to begin with). perfTierOverhead and
   *   metadataOverhead are also excluded from the OSA Overheads bucket (per explicit requirement -
   *   they're not folded into Used by Workloads either, so they land in Remaining instead), leaving
   *   only operationsReserve as the OSA Overheads component.
   */
  getStorageUsageBreakdown(siteClusters: PcsSizingEngineResponse.Cluster[]): UsageBreakdown | undefined {
    siteClusters = (siteClusters ?? []).filter(c => c.clusterType !== 'CON');
    if (!siteClusters || siteClusters.length === 0) return undefined;
    const nonWitnessClusters = siteClusters.filter(c => c.siteType !== 'WITNESS');
    const clustersToCount = nonWitnessClusters.length > 0 ? nonWitnessClusters : siteClusters;
    const raw = this.getRawCapacityValue(siteClusters);
    const unit = clustersToCount[0]?.clusterConfiguration?.rawCapacity?.units || 'TB';
    const isOsa = this.isOsaArchitecture(clustersToCount);
    let usedByWorkloads = 0;
    let objectOverheadSum = 0;
    let fileSystemOverheadSum = 0;
    let perfTierOverheadSum = 0;
    let metadataOverheadSum = 0;
    let additionalReserveSum = 0;
    clustersToCount.forEach(c => {
      additionalReserveSum += this.getEsaAdditionalReserve(c);
      const overheads = c.diskSpaceUsageOverheads;
      const objectOverhead = parseFloat(overheads?.objectOverhead?.value || '0');
      objectOverheadSum += objectOverhead;
      const isEsa = this.pcsSizer.vsanArchType(c.vsanArchitecture) === 'vSAN ESA';
      const esaDemand = parseFloat(c.clusterConfiguration?.rawStorageWithOverheads?.value ?? '');
      usedByWorkloads += isEsa && Number.isFinite(esaDemand) && esaDemand >= 0
        ? esaDemand
        : parseFloat(overheads?.rawStorage?.value || '0') + objectOverhead;
      if (isOsa) {
        if (overheads?.fileSystemOverhead) fileSystemOverheadSum += parseFloat(overheads.fileSystemOverhead.value);
        if (overheads?.diskFormatOverhead) usedByWorkloads += parseFloat(overheads.diskFormatOverhead.value);
        if (overheads?.checksumOverhead) usedByWorkloads += parseFloat(overheads.checksumOverhead.value);
        if (overheads?.compressionOnlyOverhead) usedByWorkloads += parseFloat(overheads.compressionOnlyOverhead.value);
        if (overheads?.perfTierOverhead) perfTierOverheadSum += parseFloat(overheads.perfTierOverhead.value);
        if (c.clusterConfiguration?.metadataOverhead) metadataOverheadSum += parseFloat(c.clusterConfiguration.metadataOverhead.value);
      }
    });
    usedByWorkloads += fileSystemOverheadSum;
    const overheads = this.getStorageOverheadsSum(siteClusters) - objectOverheadSum - fileSystemOverheadSum - perfTierOverheadSum - metadataOverheadSum + additionalReserveSum;
    // Effective used capacity can exceed usable capacity when a domain is overcommitted - cap it
    // at what's actually usable so Overheads + Used + Remaining never exceeds RAW (100%) in the bar.
    usedByWorkloads = Math.min(usedByWorkloads, Math.max(raw - overheads, 0));
    const remaining = Math.max(raw - overheads - usedByWorkloads, 0);
    return {
      raw,
      usable: raw - overheads,
      unit,
      overheads,
      usedByWorkloads,
      remaining
    };
  }
}
